import {
    Component,
    computed,
    input,
    numberAttribute,
    Signal,
    TemplateRef,
    ChangeDetectionStrategy,
} from "@angular/core";
import { PlacementArray } from "@ng-bootstrap/ng-bootstrap";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Item } from "../../pojo/Item";
import { TooltipResponse, TooltipService } from "../../services/tooltip.service";
import { S25Util } from "../../util/s25-util";
import { ModalService } from "../modal/modal.service";
import { ItemUtil } from "./item.util";
import { S25ItemComponent, S25ItemComponentTooltipParams } from "./s25.item.component";

export type S25ItemBean = {
    itemId?: number | string;
    itemTypeId?: ItemUtil.SupportedItemType;
    itemName?: string; // If not set, the component will fetch it
    itemLabel?: string;
    itemType?: string;
    isInactive?: boolean;
    includeTypeIcon?: boolean;
    onShow?: () => void;
    popoverTemplate?: TemplateRef<unknown>;
    popoverClass?: string;
    startDt?: Date;
    itemId2?: number;
    data?: TooltipResponse & {
        timeFormat?: string;
        dateFormat?: string;
        item?: { image_id: number };
    };
    reservation_id?: number;
    reservationId?: number;
    formalName?: string;
    formal_name?: string;
    placement?: PlacementArray;

    [key: PropertyKey]: any;
};

function normalizeItemProps(bean: S25ItemBean) {
    if (bean && S25Util.isUndefined(bean.itemTypeId)) {
        return ["event", "space", "room", "location", "resource", "org", "organization", "acct", "contact", "cont"]
            .flatMap((type) => ["Id", "_id"].map((suff) => ({ type, suff })))
            .filter(({ type, suff }) => S25Util.isDefined(bean[type + suff]))
            .reduce((bean, { type, suff }) => {
                const prop = type + suff;
                let itemTypeId: Item.Id;
                if (type === "event") {
                    itemTypeId = Item.Ids.Event;
                } else if (type === "resource") {
                    itemTypeId = Item.Ids.Resource;
                } else if (type.indexOf("org") > -1 || type === "acct") {
                    itemTypeId = Item.Ids.Organization;
                } else if (type.indexOf("cont") > -1) {
                    itemTypeId = Item.Ids.Contact;
                } else {
                    itemTypeId = Item.Ids.Location;
                }

                return {
                    itemTypeId: itemTypeId,
                    itemId: bean.itemId || bean[prop],
                    itemId2: bean.itemId2 || bean.reservation_id || bean.reservationId,
                    itemName: bean.itemName || bean[type + "Name"] || bean[type + "_name"],
                    itemLabel:
                        bean.itemLabel ||
                        bean[type + "Title"] ||
                        bean[type + "_title"] ||
                        bean.formalName ||
                        bean.formal_name,
                };
            }, bean);
    } else {
        return bean;
    }
}

/**
 * Unwraps a bean into granular inputs for {@link S25ItemComponent}. Ideally we move away from this at some point,
 * but for now it at least encapsulates the normalization in one spot.
 */
@TypeManagerDecorator("s25-item-unwrapper")
@Component({
    selector: "s25-item-unwrapper",
    template: `
        <s25-item
            #s25Item
            [itemId]="itemId()"
            [itemTypeId]="itemTypeId()!"
            [itemId2]="itemId2()"
            [itemName]="itemName()"
            [itemLabel]="itemLabel()"
            [includeTypeIcon]="includeTypeIcon() ?? modelBean()?.includeTypeIcon ?? false"
            [placement]="placement() ?? modelBean().placement"
            [popoverClass]="modelBean().popoverClass"
            [popoverTemplate]="modelBean().popoverTemplate"
            [inactive]="isInactive()"
            [ariaLabel]="ariaLabel()"
            [tooltipService]="dataService()"
            [tooltipContext]="dataServiceContext()"
            [newTab]="newTab()"
            [hideLabel]="hideLabel() ?? modelBean().hideLabel ?? false"
            [contextMenuBean]="{
                startDt: modelBean().startDt,
            }"
            [detailsOverrideFn]="detailsOverrideFn() ?? (itemTypeId() == Item.Ids.Task ? createEditTask : undefined)"
        >
            <ng-content>
                <!-- not exactly ideal, but it makes the content projection "just work" -->
                <span class="s25-item-name s25-item-link">{{ s25Item.itemName() }}</span>
            </ng-content>
        </s25-item>
    `,
    styles: `
        :host {
            display: inline-block;
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [S25ItemComponent],
})
export class S25ItemUnwrapperComponent<TTooltipContext = unknown> {
    readonly modelBean = input.required<S25ItemBean>({});
    readonly itemTypeIdInput: Signal<ItemUtil.SupportedItemType | undefined> = input(undefined, {
        alias: "itemTypeId",
        transform: numberAttribute,
    });
    readonly ariaLabel = input<string>();
    readonly dataService =
        input<
            (params: S25ItemComponentTooltipParams<TTooltipContext>) => ReturnType<typeof TooltipService.getTooltip>
        >();
    readonly dataServiceContext = input<TTooltipContext>();
    readonly inactive = input<boolean>(false);
    readonly newTab = input<boolean>(false);
    readonly hideLabel = input<boolean | "legacy">("legacy");
    readonly placement = input<PlacementArray>();
    readonly includeTypeIcon = input<boolean>(false);
    readonly detailsOverrideFn = input<($event: KeyboardEvent | TouchEvent | MouseEvent) => Promise<unknown>>();

    protected readonly normalizedItemProps = computed(() =>
        normalizeItemProps({
            ...this.modelBean(),
            ...(this.itemTypeIdInput() ? { itemTypeId: this.itemTypeIdInput() } : {}),
        }),
    );
    readonly itemId = computed(() => this.normalizedItemProps().itemId);
    readonly itemId2 = computed(() => this.normalizedItemProps().itemId2);
    readonly itemTypeId = computed(() => {
        const itemTypeId = this.normalizedItemProps().itemTypeId;
        if (!itemTypeId) {
            // TODO: throw an error?
            console.error("Unexpected missing itemTypeId", this.modelBean());
        }
        return itemTypeId;
    });
    readonly itemName = computed(() => this.normalizedItemProps().itemName);
    readonly itemLabel = computed(() => this.normalizedItemProps().itemLabel);
    readonly isInactive = computed(() =>
        S25Util.coalesce(this.modelBean().isInactive, S25Util.toBool(this.inactive())),
    );

    protected readonly Item = Item;
    protected readonly createEditTask = () =>
        ModalService.modal("create-edit-task", {
            type: "edit",
            taskType: this.modelBean().taskType,
            taskId: S25Util.parseInt(this.itemId()),
            eventId: this.modelBean().eventId,
            taskActionsCell: this.modelBean() as any,
            listModelBean: this.modelBean().listModelBean,
        });
}
